/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
import 'bootstrap/dist/css/bootstrap.min.css';
import { useContext, useEffect, useRef, useState } from 'react';
import { Badge, Button, Container, Spinner, Stack, Table } from 'react-bootstrap';
import { useTheme } from 'styled-components';
import {
    getProgressGitStatus,
    updateProgressBatched,
    publishProgressToS3,
    pullProgressFromS3,
    GitCommitEntry,
    ProgressHistoryRow,
    ProgressFailedRow,
    ProgressData,
} from '../../data';
import { StatusContext } from '../StatusContext';

interface MergedRow {
    hash: string;
    shortHash: string;
    date: string;
    message: string;
    serial?: string;
    present: boolean;
    failedError?: string;
    failedTries?: number;
    fingerprints?: number;
    marketingRiskRoutes?: number;
    engineeringMatches?: number;
}

function getMarketingRiskRoutes(data: ProgressData): number {
    if (!Array.isArray(data.supercats)) {
        return 0;
    }
    const allEntry = data.supercats.find((s) => s.key === 'ALL');
    if (allEntry) {
        return allEntry.match_count || 0;
    }
    // Backward-compat fallback for snapshots without ALL.
    return data.supercats
        .filter((s) => s.key !== 'ALL')
        .reduce((sum, s) => sum + (s.match_count || 0), 0);
}

function ProgressRefreshPage() {
    const theme = useTheme() as any;
    const statusContext = useContext(StatusContext);
    const textStyle = { color: theme?.colors?.text };

    const [rows, setRows] = useState<MergedRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [forcing, setForcing] = useState(false);
    const [batchInfo, setBatchInfo] = useState<string>('');
    const [pushing, setPushing] = useState(false);
    const [pulling, setPulling] = useState(false);
    const [replacing, setReplacing] = useState(false);

    const stopReqRef = useRef(false);

    async function reload() {
        setLoading(true);
        try {
            const { gitLog, history, failed } = await getProgressGitStatus();
            const historyMap = new Map<string, ProgressHistoryRow>(history.map(h => [h.hash, h]));
            const failedMap = new Map<string, ProgressFailedRow>((failed || []).map(f => [f.hash, f]));
            const merged: MergedRow[] = gitLog.map((c: GitCommitEntry) => {
                const h = historyMap.get(c.hash);
                const f = failedMap.get(c.hash);
                let fingerprints: number | undefined;
                let marketingRiskRoutes: number | undefined;
                let engineeringMatches: number | undefined;
                if (h) {
                    try {
                        const d: ProgressData = JSON.parse(h.data);
                        fingerprints = d.total_fingerprints;
                        marketingRiskRoutes = getMarketingRiskRoutes(d);
                        engineeringMatches = d.total_matches;
                    } catch (_e) {}
                }
                return {
                    hash: c.hash,
                    shortHash: c.shortHash,
                    date: c.date,
                    message: c.message,
                    serial: c.serial,
                    present: !!h,
                    failedError: h ? undefined : f?.error,
                    failedTries: h ? undefined : f?.tries,
                    fingerprints,
                    marketingRiskRoutes,
                    engineeringMatches,
                };
            });
            setRows(merged);
        } catch (err: any) {
            statusContext.postError(`Failed to load progress status: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Both buttons run the same batched loop: the server snapshots a few
    // commits per call, and anything cached so far survives an early stop.
    async function runUpdate(opts: { force?: boolean; retryFailed?: boolean }, label: string) {
        stopReqRef.current = false;
        setBatchInfo('starting...');
        try {
            const result = await updateProgressBatched({
                ...opts,
                onProgress: (done, todo) => setBatchInfo(`${done} / ${todo} commits`),
                shouldStop: () => stopReqRef.current,
            });
            const stopped = stopReqRef.current && result.remaining > 0;
            statusContext.postInfo(
                `${label}: ${result.processed} processed, ${result.skipped} cached, ${result.errors} errors`
                + (stopped ? `, ${result.remaining} left (stopped)` : '')
            );
            await reload();
        } catch (err: any) {
            statusContext.postError(`${label} failed: ${err.message}`);
        } finally {
            setBatchInfo('');
        }
    }

    async function handleUpdate() {
        setUpdating(true);
        try {
            await runUpdate({ retryFailed: true }, 'Progress updated');
        } finally {
            setUpdating(false);
        }
    }

    async function handleForceUpdate() {
        setForcing(true);
        try {
            await runUpdate({ force: true }, 'Force refresh complete');
        } finally {
            setForcing(false);
        }
    }

    async function handlePush() {
        setPushing(true);
        try {
            const s3path = await publishProgressToS3();
            statusContext.postInfo(`Progress published to ${s3path}`);
        } catch (err: any) {
            statusContext.postError(`Push failed: ${err.message}`);
        } finally {
            setPushing(false);
        }
    }

    async function handlePull() {
        setPulling(true);
        try {
            const count = await pullProgressFromS3();
            statusContext.postInfo(`Pulled ${count} rows from S3`);
            await reload();
        } catch (err: any) {
            statusContext.postError(`Pull failed: ${err.message}`);
        } finally {
            setPulling(false);
        }
    }

    async function handleFullReplaceFromS3() {
        setReplacing(true);
        try {
            const count = await pullProgressFromS3(true);
            statusContext.postInfo(`Replaced local progress table from S3 (${count} rows)`);
            await reload();
        } catch (err: any) {
            statusContext.postError(`Full S3 reload failed: ${err.message}`);
        } finally {
            setReplacing(false);
        }
    }

    const missing = rows.filter(r => !r.present).length;
    const failedCount = rows.filter(r => !r.present && r.failedError).length;
    const busy = updating || forcing;

    return (
        <Container fluid className="pt-3 px-4">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <h4 style={textStyle}>Refresh Progress Statistics</h4>
                    <div className="text-muted small">
                        {loading
                            ? 'Loading...'
                            : `${rows.length} commits in git history, ${missing} missing statistics`
                              + (failedCount ? ` (${failedCount} previously failed)` : '')}
                    </div>
                    {batchInfo && (
                        <div className="small" style={{ color: theme?.colors?.action }}>
                            Snapshotting {batchInfo} — cached as it goes, safe to stop
                        </div>
                    )}
                    <div className="text-muted small">
                        Marketing routes match Dash Summary ALL; engineering matches are raw finger_match rows.
                    </div>
                </div>
                <Stack direction="horizontal" gap={2}>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={updating || forcing || loading}
                        onClick={handleUpdate}
                    >
                        {updating && <Spinner size="sm" className="me-1" />}
                        Update
                    </Button>
                    <Button
                        variant="outline-warning"
                        size="sm"
                        disabled={forcing || updating || loading}
                        onClick={handleForceUpdate}
                        title="Recompute all commits from git and overwrite existing progress rows"
                    >
                        {forcing && <Spinner size="sm" className="me-1" />}
                        Force Update All
                    </Button>
                    {busy && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => { stopReqRef.current = true; }}
                            title="Stop after the current batch — snapshots already taken stay cached"
                        >
                            Stop
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={pushing || loading}
                        onClick={handlePush}
                    >
                        {pushing && <Spinner size="sm" className="me-1" />}
                        Push to S3
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={pulling || loading}
                        onClick={handlePull}
                    >
                        {pulling && <Spinner size="sm" className="me-1" />}
                        Pull from S3
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={replacing || loading}
                        onClick={handleFullReplaceFromS3}
                        title="Clear and fully reload local progress table from S3"
                    >
                        {replacing && <Spinner size="sm" className="me-1" />}
                        Reload Full Local from S3
                    </Button>
                </Stack>
            </div>

            {loading ? (
                <div className="text-muted">Loading...</div>
            ) : (
                <Table size="sm" hover variant="dark" style={{ fontSize: '0.85rem' }}>
                    <thead>
                        <tr>
                            <th>Hash</th>
                            <th>Date</th>
                            <th>Message</th>
                            <th>Tags</th>
                            <th>Fingerprints</th>
                            <th title="Summed per-category/supercat route attribution (matches Dash Summary ALL)">Risk Routes (Marketing)</th>
                            <th title="Raw count of rows in finger_match">Matches (Engineering)</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.hash}>
                                <td><code>{r.shortHash}</code></td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    {new Date(r.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                </td>
                                <td style={{ maxWidth: '28rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {r.message}
                                </td>
                                <td>
                                    {r.serial && <Badge bg="info">release/{r.serial}</Badge>}
                                </td>
                                <td>{r.fingerprints != null ? r.fingerprints.toLocaleString() : '—'}</td>
                                <td>{r.marketingRiskRoutes != null ? r.marketingRiskRoutes.toLocaleString() : '—'}</td>
                                <td>{r.engineeringMatches != null ? r.engineeringMatches.toLocaleString() : '—'}</td>
                                <td>
                                    {r.present && <Badge bg="success">Present</Badge>}
                                    {!r.present && r.failedError && (
                                        <Badge bg="danger" title={`${r.failedTries} attempt(s): ${r.failedError}`}>Failed</Badge>
                                    )}
                                    {!r.present && !r.failedError && <Badge bg="warning" text="dark">Missing</Badge>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Container>
    );
}

export default ProgressRefreshPage;
